import { useEffect, useState, useCallback } from "react";
import { api, API_BASE, formatApiError, getToken } from "@/lib/api";
import { fmtRelative } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Truck, Plus, RefreshCw, Printer, X, ExternalLink, PackageOpen, PackageCheck, Clock,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { STATUS_PILL, shipmentLabel } from "@/pages/returns/shipmentStatusMap";

const GLS_TRACK_BASE = "https://gls-group.eu/RO/ro/urmarire-colet?match=";

function StatusPill({ status, direction }) {
  const pill = STATUS_PILL[status] || STATUS_PILL.created;
  return (
    <Badge variant="outline" className={`gap-1.5 ${pill.color}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${pill.dot}`} />
      {shipmentLabel(status, direction)}
    </Badge>
  );
}

function DirectionSection({ serviceId, direction, shipment, busy, setBusy, reload, canEdit }) {
  const [manualAwb, setManualAwb] = useState("");
  const isInbound = direction === "inbound";
  const active = shipment && shipment.status !== "cancelled";

  const onCreate = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/service/${serviceId}/shipments`, { direction, courier: "gls" });
      toast.success(`AWB ${r.data?.shipment?.awb} generat`);
      await reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  const onAttachManual = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/service/${serviceId}/shipments/manual`, { direction, awb: manualAwb.trim(), courier: "gls" });
      toast.success(`AWB ${r.data?.shipment?.awb} atașat`);
      setManualAwb("");
      await reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  const onSync = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/service/${serviceId}/shipments/${shipment.id}/sync`);
      if (r.data?.pending_tracking) toast.info(r.data.message);
      else if (r.data?.ok) toast.success("Status actualizat");
      else toast.error(r.data?.error || "Sync nereușit");
      await reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  const onCancel = async () => {
    setBusy(true);
    try {
      await api.delete(`/service/${serviceId}/shipments/${shipment.id}`);
      toast.success("Colet anulat");
      await reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  const printLabel = () => {
    const url = `${API_BASE}/service/${serviceId}/shipments/${shipment.id}/label`;
    fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then((r) => {
        if (!r.ok) throw new Error("Eticheta nu a putut fi descărcată");
        return r.blob();
      })
      .then((b) => window.open(URL.createObjectURL(b), "_blank"))
      .catch((e) => toast.error(e.message));
  };

  const Icon = isInbound ? PackageOpen : PackageCheck;
  return (
    <div className="space-y-3" data-testid={`service-shipment-${direction}`}>
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-primary" />
        <span className="text-sm font-semibold">
          {isInbound ? "Ridicare de la client" : "Trimitere produs reparat"}
        </span>
      </div>

      {active ? (
        <div className="rounded-lg border border-border bg-secondary/40 p-3 space-y-2.5">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-primary font-semibold">
                AWB · {(shipment.courier || "GLS").toUpperCase()}{shipment.manual ? " · atașat manual" : ""}
              </div>
              <div className="font-mono text-lg font-bold" data-testid={`service-awb-${direction}`}>{shipment.awb}</div>
            </div>
            <StatusPill status={shipment.status} direction={direction} />
          </div>
          {shipment.last_status_at && (
            <div className="text-[11px] text-muted-foreground flex items-center gap-1">
              <Clock className="h-3 w-3" /> Ultim status: {fmtRelative(shipment.last_status_at)}
            </div>
          )}
          <div className="flex flex-wrap gap-1.5">
            {!shipment.manual && (
              <Button onClick={printLabel} variant="outline" size="sm" className="h-8" data-testid={`service-print-${direction}`}>
                <Printer className="h-3.5 w-3.5 mr-1.5" /> Etichetă
              </Button>
            )}
            <Button onClick={onSync} variant="outline" size="sm" className="h-8" disabled={busy} data-testid={`service-sync-${direction}`}>
              <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${busy ? "animate-spin" : ""}`} /> Sync
            </Button>
            <Button asChild variant="outline" size="sm" className="h-8">
              <a href={`${GLS_TRACK_BASE}${shipment.awb}`} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-3.5 w-3.5 mr-1.5" /> GLS
              </a>
            </Button>
            {canEdit && !["delivered"].includes(shipment.status) && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-8 text-red-600 hover:bg-red-500/10" disabled={busy} data-testid={`service-cancel-${direction}`}>
                    <X className="h-3.5 w-3.5 mr-1.5" /> Anulează
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Anulezi acest colet?</AlertDialogTitle>
                    <AlertDialogDescription>
                      AWB {shipment.awb} va fi anulat{shipment.manual ? " doar în platformă (în MyGLS îl anulezi manual)" : " și la GLS (dacă nu a fost încă scanat)"}.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Renunță</AlertDialogCancel>
                    <AlertDialogAction onClick={onCancel} className="bg-red-600 hover:bg-red-700" data-testid={`service-cancel-confirm-${direction}`}>
                      Da, anulează
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </div>
      ) : canEdit ? (
        <div className="space-y-2">
          <Button onClick={onCreate} disabled={busy} size="sm" className="w-full" data-testid={`service-create-awb-${direction}`}>
            <Plus className="h-4 w-4 mr-2" />
            {busy ? "Se generează..." : isInbound ? "Cheamă curier GLS la client" : "Generează AWB trimitere"}
          </Button>
          <div className="flex gap-1.5">
            <Input
              placeholder="Sau AWB existent din MyGLS"
              value={manualAwb}
              onChange={(e) => setManualAwb(e.target.value)}
              className="h-8 font-mono text-xs"
              data-testid={`service-manual-awb-input-${direction}`}
            />
            <Button
              variant="outline" size="sm" className="h-8 shrink-0"
              disabled={busy || !manualAwb.trim()}
              onClick={onAttachManual}
              data-testid={`service-manual-awb-btn-${direction}`}
            >
              Atașează
            </Button>
          </div>
        </div>
      ) : (
        <div className="text-xs text-muted-foreground">Fără AWB</div>
      )}
    </div>
  );
}

export const ServiceShipmentsCard = ({ serviceId, onAfterChange }) => {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission("edit_service");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    try {
      const r = await api.get(`/service/${serviceId}/shipments`);
      setData(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
    onAfterChange?.();
  }, [serviceId]);

  useEffect(() => { reload(); }, [reload]);

  if (loading) return <Card className="p-6"><Skeleton className="h-40" /></Card>;

  return (
    <Card className="p-6 bg-card border-border" data-testid="service-shipments-card">
      <h3 className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-4 flex items-center gap-1.5">
        <Truck className="h-3.5 w-3.5" /> Logistică GLS
      </h3>
      <div className="space-y-5">
        <DirectionSection
          serviceId={serviceId} direction="inbound" shipment={data?.inbound}
          busy={busy} setBusy={setBusy} reload={reload} canEdit={canEdit}
        />
        <div className="border-t border-border" />
        <DirectionSection
          serviceId={serviceId} direction="outbound" shipment={data?.outbound}
          busy={busy} setBusy={setBusy} reload={reload} canEdit={canEdit}
        />
        {data?.events?.length > 0 && (
          <div className="pt-3 border-t border-border">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-2">
              Istoric colete ({data.events.length})
            </div>
            <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
              {data.events.map((ev) => {
                const pill = STATUS_PILL[ev.status] || STATUS_PILL.created;
                return (
                  <div key={ev.id} className="flex gap-2 text-xs">
                    <span className={`h-1.5 w-1.5 rounded-full mt-1.5 shrink-0 ${pill.dot}`} />
                    <div className="min-w-0">
                      <div className="font-medium">
                        {ev.description}
                        <span className="text-muted-foreground font-normal"> · {ev.direction === "inbound" ? "ridicare" : "trimitere"}</span>
                      </div>
                      <div className="text-muted-foreground">{fmtRelative(ev.at)}{ev.actor ? ` · ${ev.actor}` : ""}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

export default ServiceShipmentsCard;
